import { useCallback, useRef, useState } from 'react';
import { buildReportPrompt } from './promptTemplate';
import { isValidEmail } from '../../../Utils/Utils';

/**
 * Reports mode state machine — Atlas Chat Reports.
 *
 * Flow: filter form → review prompt → generate preview (deep-insight agent
 * over websocket) → confirm → schedule.
 *
 *   States: 'idle' | 'filtering' | 'reviewing' | 'previewing' | 'confirming'
 *           | 'scheduling'
 *
 * The send to the agent is delegated to the `onSendToAgent` callback (stored in
 * a ref so it always sees the latest closure). The preview renders in the chat
 * thread; `onPreviewResponse()` is called by the chat response handler to
 * advance previewing → confirming.
 */

const EMPTY_FILTERS = Object.freeze({
  sectors: [],
  ipoStatus: [],
  revenueRange: [],
  numEmployees: [],
  fundingType: [],
  fundingTotal: [],
  context: '',
});

const browserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

// Earliest selectable start: now + 1 hour, as a `datetime-local` string
// ("YYYY-MM-DDTHH:mm") in the user's local time.
export const minStartLocal = () => {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
};

// start_at is valid only if it is at least one hour in the future.
const isStartValid = (startAt) => {
  if (!startAt) return false;
  const picked = new Date(startAt);
  if (Number.isNaN(picked.getTime())) return false;
  return picked.getTime() >= Date.now() + 60 * 60 * 1000;
};

const hasValidRecipient = (toEmail) =>
  String(toEmail || '')
    .split(',')
    .map((e) => e.trim())
    .some((e) => e && isValidEmail(e));

const defaultSchedule = ({ name = '', toEmail = '' } = {}) => ({
  name,
  frequency: 'once',
  startAt: '',
  timezone: browserTimezone(),
  isActive: true,
  toEmail,
  ccEmail: '',
  bccEmail: '',
});

const useReportsMode = ({ onSendToAgent, onScheduleSubmit, userEmail = '' } = {}) => {
  const [state, setState] = useState('idle');
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [schedule, setSchedule] = useState(null);
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);
  const onSendToAgentRef = useRef(onSendToAgent);
  onSendToAgentRef.current = onSendToAgent;
  const onScheduleSubmitRef = useRef(onScheduleSubmit);
  onScheduleSubmitRef.current = onScheduleSubmit;

  const open = useCallback(() => {
    setFilters({ ...EMPTY_FILTERS });
    setGeneratedPrompt('');
    setSchedule(null);
    setState('filtering');
  }, []);

  const cancel = useCallback(() => {
    setState('idle');
    setFilters({ ...EMPTY_FILTERS });
    setGeneratedPrompt('');
    setSchedule(null);
    setScheduleSubmitting(false);
    setScheduleError(null);
  }, []);

  const toggleFilter = useCallback((dimension, value) => {
    setFilters((prev) => {
      const current = prev[dimension] || [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [dimension]: next };
    });
  }, []);

  const setContext = useCallback((value) => {
    setFilters((prev) => ({ ...prev, context: value }));
  }, []);

  // Gate for "Continue": at least one Sector.
  const canProceed = filters.sectors.length > 0;

  // Filter form → review: build the prompt from the current selections.
  const proceedToReview = useCallback(() => {
    if (!canProceed) return;
    setGeneratedPrompt(buildReportPrompt(filters));
    setState('reviewing');
  }, [canProceed, filters]);

  // Reject at review or confirm → back to the filter form (selections kept).
  const backToFilter = useCallback(() => {
    setState('filtering');
  }, []);

  // Review approve → generate preview via the deep-insight agent.
  const approvePrompt = useCallback(() => {
    setState('previewing');
    if (onSendToAgentRef.current) onSendToAgentRef.current(generatedPrompt, filters);
  }, [generatedPrompt, filters]);

  // Called by the chat response handler; only advances if a preview is pending.
  const onPreviewResponse = useCallback(() => {
    setState((prev) => (prev === 'previewing' ? 'confirming' : prev));
  }, []);

  // Confirm preview → schedule step. Seed the form from the selections + user.
  const confirmPreview = useCallback(() => {
    const sectors = filters.sectors || [];
    const name = sectors.length ? `Sector Report: ${sectors.join(', ')}` : 'Sector Report';
    setSchedule(defaultSchedule({ name, toEmail: userEmail }));
    setScheduleError(null);
    setScheduleSubmitting(false);
    setState('scheduling');
  }, [filters, userEmail]);

  const updateSchedule = useCallback((patch) => {
    setSchedule((prev) => ({ ...(prev || defaultSchedule()), ...patch }));
  }, []);

  const canSchedule =
    !!schedule &&
    schedule.name.trim().length > 0 &&
    isStartValid(schedule.startAt) &&
    hasValidRecipient(schedule.toEmail);

  const submitSchedule = useCallback(async () => {
    if (!schedule || !canSchedule) return;
    setScheduleSubmitting(true);
    setScheduleError(null);
    try {
      if (onScheduleSubmitRef.current) {
        await onScheduleSubmitRef.current(schedule, generatedPrompt);
      }
      cancel();
    } catch (err) {
      setScheduleSubmitting(false);
      setScheduleError(
        err?.response?.data?.detail || err?.message || 'Failed to schedule report',
      );
    }
  }, [schedule, canSchedule, generatedPrompt, cancel]);

  return {
    state,
    filters,
    generatedPrompt,
    schedule,
    scheduleSubmitting,
    scheduleError,
    canProceed,
    canSchedule,
    isOpen:
      state === 'filtering' ||
      state === 'reviewing' ||
      state === 'previewing' ||
      state === 'confirming' ||
      state === 'scheduling',
    open,
    cancel,
    toggleFilter,
    setContext,
    proceedToReview,
    backToFilter,
    approvePrompt,
    onPreviewResponse,
    confirmPreview,
    updateSchedule,
    submitSchedule,
  };
};

export default useReportsMode;
